/**
 * Display-mode session, kept free of React and of the API client.
 *
 * The route `/display/<tv-name>` starts a session. The pages it rotates to
 * live at their own paths, so the session has to outlive that route. Callers
 * read it with `useSyncExternalStore` and advance it from a timer.
 *
 * ADR-0019: a reload under `/display/` is also what tells Firebase to read
 * the display account's persistent session. That decision is `isDisplayBootPath`.
 */

import { type Theme } from './theme.ts'

/**
 * How often a display refetches the queries on screen.
 *
 * A product decision, not a design value, so it lives here rather than in the
 * design system. One minute: a TV has nobody to refresh it, and a report
 * should not sit stale for the whole playlist. `display-mode.test.ts` pins
 * the value, so changing it stays a deliberate act.
 */
export const DISPLAY_REFRESH_MS = 60 * 1000

/** How long a display boot waits for its first session callback before showing sign-in. */
export const DISPLAY_AUTH_WAIT_MS = 15_000

/**
 * How often a display retries a playlist it could not show.
 *
 * A product decision, like `IDLE_LIMIT_MS`. The screen has no control to
 * press. The same wait covers a display that is not set up and a network
 * failure, so a TV left on the offline notice recovers on its own.
 */
export const DISPLAY_RETRY_MS = 30 * 1000

/**
 * A TV name the playlist endpoint accepts.
 *
 * The server rejects anything else with 400. Checking here avoids that
 * request and shows the not-set-up screen immediately.
 */
const TV_NAME = /^[a-z0-9][a-z0-9-]{0,39}$/

/** One page in a playlist, already checked. */
export interface DisplayPage {
  path: string
  seconds: number
}

/**
 * Where a display stands.
 *
 * `refreshing` keeps the last page on screen while the next copy of the
 * playlist loads, so the TV does not flash empty between cycles. `since`
 * is when the offline or missing notice went up, so a re-render does not
 * restart the retry wait.
 */
export type DisplaySession =
  | { kind: 'off' }
  | { kind: 'booting'; name: string }
  | { kind: 'missing'; name: string; since: number }
  | { kind: 'offline'; name: string; since: number }
  | {
      kind: 'playing'
      name: string
      pages: DisplayPage[]
      index: number
      shownAt: number
      theme: Theme
    }
  | {
      kind: 'refreshing'
      name: string
      pages: DisplayPage[]
      index: number
      shownAt: number
      theme: Theme
    }

/** A clock the rotation reads. Tests pass `ManualClock`. */
export interface DisplayClock {
  now(): number
  after(ms: number, run: () => void): () => void
}

const systemClock: DisplayClock = {
  now: () => Date.now(),
  after(ms, run) {
    const id = setTimeout(run, ms)

    return () => {
      clearTimeout(id)
    }
  }
}

let clock: DisplayClock = systemClock

/**
 * The clock the rotation is using.
 *
 * Read inside the effect that arms the timer, not during render, so a test
 * can install a manual clock before the effect runs.
 *
 * @returns The current clock.
 */
export function getDisplayClock(): DisplayClock {
  return clock
}

/**
 * Install a clock, or put the system clock back.
 *
 * @param next - The clock to use. Omit it to restore the system clock.
 */
export function setDisplayClock(next: DisplayClock = systemClock): void {
  clock = next
}

const OFF: DisplaySession = { kind: 'off' }

let session: DisplaySession = OFF

const listeners = new Set<() => void>()

/**
 * The session as it stands.
 *
 * The same object until a dispatch, so `useSyncExternalStore` can compare it
 * with `Object.is`.
 *
 * @returns The current session.
 */
export function getDisplaySession(): DisplaySession {
  return session
}

/**
 * Hear the next session.
 *
 * @param listener - Called after every change.
 * @returns Unsubscribe.
 */
export function subscribeDisplay(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * Replace the session.
 *
 * The same object is a no-op, so a route effect can re-apply `startSession`
 * for the TV it already started without looping.
 *
 * @param next - The session to publish.
 */
export function dispatchDisplay(next: DisplaySession): void {
  if (next === session) {
    return
  }
  session = next
  for (const listener of listeners) {
    listener()
  }
}

/** Drop the session. A sign-out calls this so the next person inherits nothing. */
export function resetDisplaySession(): void {
  dispatchDisplay(OFF)
}

/**
 * Whether a pathname is a single display segment.
 *
 * Any segment, including one the playlist endpoint would reject. A reload of
 * `/display/<name>` is what reads the display account's persistent session,
 * and a bad name still has to be able to clear that session (ADR-0019).
 *
 * @param pathname - The pathname, without a search string.
 * @returns The segment, or null when this is not a display boot.
 */
export function displayNameFromPath(pathname: string): string | null {
  const match = /^\/display\/([^/]+)$/.exec(pathname)
  const segment = match?.[1]
  if (segment === undefined) {
    return null
  }
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Whether this page load should read the display account's persistent session.
 *
 * @param pathname - `window.location.pathname` at construction.
 * @returns True for `/display/<name>`.
 */
export function isDisplayBootPath(pathname: string): boolean {
  return displayNameFromPath(pathname) !== null
}

/**
 * Whether `name` is a TV name the playlist endpoint accepts.
 *
 * @param name - The route param.
 * @returns True when it matches the contract.
 */
export function isTvName(name: string): boolean {
  return TV_NAME.test(name)
}

/**
 * Whether a display is running.
 *
 * The shell reads this as well as the path, because a playing page is shown
 * at its own URL and that URL is not under `/display/`.
 *
 * @param current - The session to read.
 * @returns True once a TV has been started and not reset.
 */
export function isDisplayActive(current: DisplaySession): boolean {
  return current.kind !== 'off'
}

/**
 * The theme a running display paints, or null when the reader's own preference applies.
 *
 * Booting, missing and offline paint dark: the playlist theme is not known yet,
 * or the notice is up. Playing and refreshing paint the playlist theme. Off
 * paints nothing of its own, so the reader's preference applies again.
 *
 * @param current - The session to read.
 * @returns The override, or null when the display does not own the screen.
 */
export function displayThemeOverride(current: DisplaySession): Theme | null {
  if (current.kind === 'playing' || current.kind === 'refreshing') {
    return current.theme
  }
  if (current.kind === 'off') {
    return null
  }

  return 'dark'
}

/**
 * Start a TV, or keep the session when that TV is already running.
 *
 * Returning the same object is what keeps the route effect from restarting
 * the playlist every time it runs.
 *
 * @param current - The session now.
 * @param name - The TV name from the route.
 * @returns The next session.
 */
export function startSession(
  current: DisplaySession,
  name: string
): DisplaySession {
  if (current.kind !== 'off' && current.name === name) {
    return current
  }

  return { kind: 'booting', name }
}

/**
 * The page on screen, including while the next playlist loads.
 *
 * @param current - The session now.
 * @returns That page, or null when nothing is showing.
 */
export function currentDisplayPage(
  current: DisplaySession
): DisplayPage | null {
  if (current.kind !== 'playing' && current.kind !== 'refreshing') {
    return null
  }

  return current.pages[current.index] ?? null
}

/**
 * Move to the next page, or ask for a fresh playlist after the last one.
 *
 * A call before the page's seconds are up returns the same session, so an
 * early timer cannot skip a page.
 *
 * @param current - The session now.
 * @param now - The clock reading, in milliseconds.
 * @returns The next session.
 */
export function advanceSession(
  current: DisplaySession,
  now: number
): DisplaySession {
  if (current.kind !== 'playing') {
    return current
  }
  const page = current.pages[current.index]
  if (page === undefined) {
    return current
  }
  if (now < current.shownAt + page.seconds * 1000) {
    return current
  }
  const nextIndex = current.index + 1
  if (nextIndex < current.pages.length) {
    return { ...current, index: nextIndex, shownAt: now }
  }

  return {
    kind: 'refreshing',
    name: current.name,
    pages: current.pages,
    index: current.index,
    shownAt: current.shownAt,
    theme: current.theme
  }
}

/**
 * How long until the playing page should advance, or a notice should retry.
 *
 * @param current - The session now.
 * @param now - The clock reading, in milliseconds.
 * @returns Milliseconds to wait, or null when nothing is scheduled.
 */
export function msUntilAdvance(
  current: DisplaySession,
  now: number
): number | null {
  if (current.kind === 'playing') {
    const page = current.pages[current.index]
    if (page === undefined) {
      return null
    }

    return Math.max(0, current.shownAt + page.seconds * 1000 - now)
  }
  if (current.kind === 'offline' || current.kind === 'missing') {
    return Math.max(0, current.since + DISPLAY_RETRY_MS - now)
  }

  return null
}

/**
 * Try the playlist again.
 *
 * @param current - The session now.
 * @returns Booting for the same TV, or the same session when a retry is not due.
 */
export function retrySession(current: DisplaySession): DisplaySession {
  if (current.kind !== 'offline' && current.kind !== 'missing') {
    return current
  }

  return { kind: 'booting', name: current.name }
}

/**
 * Begin the playlist at its first page.
 *
 * A playlist for a different TV, or one with no pages, is the not-set-up
 * screen. The server is the authority for which pages exist; an empty list
 * has nothing to show.
 *
 * @param current - The session now. Only booting and refreshing accept a playlist.
 * @param playlist - The playlist the server returned, including the theme the TV paints.
 * @param now - When the first page goes up.
 * @returns The next session.
 */
export function playlistReady(
  current: DisplaySession,
  playlist: { name: string; pages: readonly DisplayPage[]; theme: Theme },
  now: number
): DisplaySession {
  if (current.kind !== 'booting' && current.kind !== 'refreshing') {
    return current
  }
  if (playlist.name !== current.name || playlist.pages.length === 0) {
    return { kind: 'missing', name: current.name, since: now }
  }

  return {
    kind: 'playing',
    name: current.name,
    pages: [...playlist.pages],
    index: 0,
    shownAt: now,
    theme: playlist.theme
  }
}

/**
 * Show the offline notice.
 *
 * Already offline keeps the same object, so the retry wait is not restarted.
 *
 * @param current - The session now.
 * @param now - When the notice goes up.
 * @returns The next session.
 */
export function markOffline(
  current: DisplaySession,
  now: number
): DisplaySession {
  if (current.kind === 'off' || current.kind === 'offline') {
    return current
  }

  return { kind: 'offline', name: current.name, since: now }
}

/**
 * Show the not-set-up notice.
 *
 * Already missing keeps the same object, so the retry wait is not restarted.
 *
 * @param current - The session now.
 * @param now - When the notice goes up.
 * @returns The next session.
 */
export function markMissing(
  current: DisplaySession,
  now: number
): DisplaySession {
  if (current.kind === 'off' || current.kind === 'missing') {
    return current
  }

  return { kind: 'missing', name: current.name, since: now }
}

/**
 * Keep the playlist after a failed refresh, or show an initial read failure.
 *
 * @param current - The session whose playlist read failed.
 * @param now - When the next cycle starts.
 * @returns The next session.
 */
export function playlistUnavailable(
  current: DisplaySession,
  now: number
): DisplaySession {
  if (current.kind === 'refreshing')
    return { ...current, kind: 'playing', index: 0, shownAt: now }
  if (current.kind === 'booting') return markOffline(current, now)
  return current
}
